import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { searchArticles } from "@/lib/content";
import type { Article } from "@/types/content";

interface SearchBoxProps {
  initialValue?: string;
  autoFocus?: boolean;
  size?: "sm" | "lg";
  placeholder?: string;
}

export default function SearchBox({
  initialValue = "",
  autoFocus = false,
  size = "sm",
  placeholder = "搜索车型、新闻或评测关键词",
}: SearchBoxProps) {
  const [value, setValue] = useState(initialValue);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const suggestions: Article[] = value.trim()
    ? searchArticles(value).slice(0, 6)
    : [];

  const submit = (keyword?: string) => {
    const q = (keyword ?? value).trim();
    if (!q) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <div ref={boxRef} className="relative w-full">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className={`flex items-center w-full bg-background-100 border border-background-300 rounded-full overflow-hidden ${
          size === "lg" ? "h-12 pl-5 pr-1.5" : "h-10 pl-4 pr-1.5"
        }`}
      >
        <i className="ri-search-line text-foreground-500 text-base mr-2" />
        <input
          value={value}
          autoFocus={autoFocus}
          onChange={(e) => {
            setValue(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 outline-none min-w-0"
        />
        <button
          type="submit"
          className={`shrink-0 flex items-center justify-center rounded-full bg-primary-600 text-background-50 hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap ${
            size === "lg" ? "h-9 px-5 text-sm" : "h-7 px-4 text-xs"
          }`}
        >
          搜索
        </button>
      </form>

      {open && value.trim() && (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 bg-background-50 border border-background-200 rounded-lg overflow-hidden shadow-lg">
          {suggestions.length > 0 ? (
            <>
              <p className="px-4 pt-3 pb-2 text-xs text-foreground-500">
                相关结果（演示实时联想）
              </p>
              <ul className="max-h-80 overflow-y-auto">
                {suggestions.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onMouseDown={() => {
                        setOpen(false);
                        navigate(`/detail/${item.id}`);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-background-100 transition-colors cursor-pointer flex items-start gap-2"
                    >
                      <i className="ri-file-text-line text-primary-500 mt-0.5" />
                      <span className="min-w-0">
                        <span className="block text-sm text-foreground-900 line-clamp-1">
                          {item.title}
                        </span>
                        <span className="block text-xs text-foreground-500 mt-0.5">
                          {item.category} · {item.brand}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="px-4 py-4 text-sm text-foreground-500">
              暂无匹配内容（演示数据），换个关键词试试
            </p>
          )}
          <button
            type="button"
            onMouseDown={() => submit()}
            className="w-full text-left px-4 py-3 text-sm text-primary-600 bg-background-100 hover:bg-background-200 transition-colors cursor-pointer border-t border-background-200"
          >
            查看“{value}”的全部搜索结果
            <i className="ri-arrow-right-line ml-1" />
          </button>
        </div>
      )}
    </div>
  );
}